import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useCoverEditor } from '../../CoverEditorContext';
import { coverEditorMessages, uploadErrorMessages } from '../../coverEditorMessages';
import { loadCoverPicture } from '../../render/loadCoverPicture';
import { paletteFromPicture } from '../../render/paletteFromPicture';
import { usePickPicture } from './usePickPicture';
import { publicCoverMessages } from '../../publicMode/publicCoverMessages';
import { useLocalPictureUpload } from '../../publicMode/useLocalPictureUpload';
import { COVER_UPLOAD_TYPES, useCoverPictureUpload } from './useReferenceUpload';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Upload tab (design cover-editor c4): an own picture as the background, free on every plan.
 * A dropped or picked file uploads at once and becomes the background; the colour swatches take
 * the colours of the picture. The place and the zoom are in the inspector of the cover. In the
 * public tool the picture stays in the browser (`useLocalPictureUpload`): there is no account.
 */
const UploadTabComponent: React.FC = () => {
  const intl = useIntl();
  const { accountId, readOnly, mode } = useCoverEditor();
  const pick = usePickPicture();
  const input = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);
  // Both hooks hold only state; the mode never changes while the editor is open.
  const account = useCoverPictureUpload(accountId);
  const local = useLocalPictureUpload();
  const { uploadPicture, isUploading, error } = mode === 'public' ? local : account;

  const take = useCallback(
    async (file: File | undefined) => {
      if (file === undefined || readOnly) {
        return;
      }
      const saved = await uploadPicture(file);
      if (saved === null) {
        return;
      }
      // The colours are a help, not a need: the picture is used with or without them.
      const palette = await loadCoverPicture(saved.url)
        .then(paletteFromPicture)
        .catch(() => []);
      pick({ kind: 'upload', fileId: saved.fileId }, saved.url, palette);
    },
    [pick, readOnly, uploadPicture]
  );
  const open = useCallback(() => input.current?.click(), []);
  const onFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // The same file can be picked again after a failure.
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
      take(file).catch(console.error);
    },
    [take]
  );
  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsOver(false);
      take(event.dataTransfer.files[0]).catch(console.error);
    },
    [take]
  );
  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(true);
  }, []);
  const onDragLeave = useCallback(() => setIsOver(false), []);

  return (
    <>
      <div
        className={clsx(styles.drop, isOver && styles.dropOver)}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <b>{intl.formatMessage(coverEditorMessages.uploadTitle)}</b>
        <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.uploadRules)}</span>
        <SMLButton size="sm" outline omitCap onClick={open} isLoading={isUploading} disabled={isUploading || readOnly}>
          {intl.formatMessage(coverEditorMessages.uploadPick)}
        </SMLButton>
      </div>
      {error !== null && (
        <span className={styles.errorText} role="alert">
          {intl.formatMessage(uploadErrorMessages[error])}
        </span>
      )}
      <span className={styles.hint}>
        {intl.formatMessage(mode === 'public' ? publicCoverMessages.uploadLocalHint : coverEditorMessages.uploadHint)}
      </span>
      <input
        ref={input}
        type="file"
        accept={COVER_UPLOAD_TYPES.join(',')}
        hidden
        aria-label={intl.formatMessage(coverEditorMessages.uploadPick)}
        onChange={onFile}
      />
    </>
  );
};

export const UploadTab = TypedMemo(UploadTabComponent);
